import React from 'react';

// The role and dates, one-line description, and accomplishment bullets that
// open every company card. Exported so the AI Projects intro can use the exact
// same layout.
export const ProjectSummary = ({
  role,
  dates,
  description,
  accomplishments = [],
  accent = '#1D1D1F',
  roleSpacing = 'mt-8',
}) => (
  <>
    <p className={`${roleSpacing} text-sm font-medium text-muted`}>
      <span className="font-bold">{role}</span>
      <span className="mx-2" aria-hidden="true">·</span>
      {dates}
    </p>

    <p className="mt-3 text-xl sm:text-2xl leading-snug text-ink font-medium tracking-tight">
      {description}
    </p>

    {accomplishments.length > 0 && (
      <ul className="mt-8 space-y-3">
        {accomplishments.map((item) => (
          <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-muted">
            <span
              className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: accent }}
              aria-hidden="true"
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )}
  </>
);

// One layout for every company on the homepage: a calm white card with the
// story on the left and the product on a brand-tinted panel on the right.
// The brand shows up only in the logo, the list markers, and that panel, so
// the page reads as one system instead of six differently colored blocks.
const ProjectFeature = ({
  logo,
  logoAlt,
  logoHeight = 36,
  role,
  dates,
  description,
  accomplishments = [],
  accent = '#1D1D1F',
  media,
  mediaBackground = '#F5F5F7',
  caseStudy, // { label, onClick }
  website, // { label, href }
}) => (
  <article className="bg-white rounded-[28px] p-6 sm:p-10 lg:p-14 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <div>
        <img src={logo} alt={logoAlt} className="object-contain object-left" style={{ height: logoHeight }} />

        <ProjectSummary
          role={role}
          dates={dates}
          description={description}
          accomplishments={accomplishments}
          accent={accent}
        />

        {(caseStudy || website) && (
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4">
            {caseStudy && (
              <button
                onClick={caseStudy.onClick}
                className="rounded-full bg-ink px-6 py-3 text-sm font-medium text-white transition-colors duration-300 hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2"
              >
                {caseStudy.label}
              </button>
            )}
            {website && (
              <a
                href={website.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-secondary hover:underline"
              >
                {website.label}
              </a>
            )}
          </div>
        )}
      </div>

      <div
        className="rounded-2xl p-6 sm:p-10 flex items-center justify-center min-h-[320px]"
        style={{ background: mediaBackground }}
      >
        {media}
      </div>
    </div>
  </article>
);

export default ProjectFeature;
